import React, { useContext, useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { VideoCallContext } from "components/context/VideoCallContext";
import Duration from "components/common/Duration";
import useIsMobile from "components/hooks/useIsMobile";

const ActiveVideoCallModule = () => {
  const { videoCallState } = useContext(VideoCallContext);
  const isMobile = useIsMobile();
  const {
    activeVideoRoom,
    token,
    renderLocation,
    isConnected,
    isConnecting,
    connectionTime,
  } = videoCallState;

  const [roomKey, setRoomKey] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState<{
    right?: number;
    bottom?: number;
    left?: number;
    top?: number;
  }>({
    right: 20,
    bottom: 20,
  });

  useEffect(() => {
    if (activeVideoRoom && token && renderLocation === "pip") {
      const newKey = `${activeVideoRoom.uuid}-${token.substring(0, 10)}`;
      if (roomKey !== newKey) {
        setRoomKey(newKey);
      }
    }
  }, [activeVideoRoom, token, renderLocation, roomKey]);

  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e: MouseEvent) => {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;

        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const pipWidth = 480;
        const pipHeight = 360;

        const clampedX = Math.max(0, Math.min(newX, windowWidth - pipWidth));
        const clampedY = Math.max(0, Math.min(newY, windowHeight - pipHeight));

        setPosition({
          left: clampedX,
          top: clampedY,
          right: undefined,
          bottom: undefined,
        });
      };

      const handleGlobalMouseUp = () => {
        setIsDragging(false);
      };

      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  if (isMobile) {
    return null;
  }

  if (!activeVideoRoom || !token) {
    return null;
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const isHeaderArea =
      target.closest(".pip-header") || target.closest(".video-control-icon");

    if (!isHeaderArea) return;

    setIsDragging(true);
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;

    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;

    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const pipWidth = 480;
    const pipHeight = 360;

    const clampedX = Math.max(0, Math.min(newX, windowWidth - pipWidth));
    const clampedY = Math.max(0, Math.min(newY, windowHeight - pipHeight));

    setPosition({
      left: clampedX,
      top: clampedY,
      right: undefined,
      bottom: undefined,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <div
      style={{
        position: "fixed",
        zIndex: 10000,
        left: position.left,
        right: position.right,
        top: position.top,
        bottom: position.bottom,
        cursor: isDragging ? "grabbing" : "default",
        display: renderLocation === "pip" ? "block" : "none",
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onTouchStart={(e) => {
        const touch = e.touches[0];
        const target = e.target as HTMLElement;
        const isHeaderArea =
          target.closest(".pip-header") ||
          target.closest(".video-control-icon");

        if (!isHeaderArea) return;

        setIsDragging(true);
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setDragOffset({
          x: touch.clientX - rect.left,
          y: touch.clientY - rect.top,
        });
      }}
      onTouchMove={(e) => {
        if (!isDragging) return;
        e.preventDefault();

        const touch = e.touches[0];
        const newX = touch.clientX - dragOffset.x;
        const newY = touch.clientY - dragOffset.y;

        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const pipWidth = 480;
        const pipHeight = 360;

        const clampedX = Math.max(0, Math.min(newX, windowWidth - pipWidth));
        const clampedY = Math.max(0, Math.min(newY, windowHeight - pipHeight));

        setPosition({
          left: clampedX,
          top: clampedY,
          right: undefined,
          bottom: undefined,
        });
      }}
      onTouchEnd={() => {
        setIsDragging(false);
      }}
    >
      <Box
        sx={{
          width: "480px",
          height: "360px",
          borderRadius: "12px",
          overflow: "hidden",
          cursor: "default",
        }}
      >
        <Box
          className="pip-header"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "6px 8px",
            // Match with the background color of the video module.
            backgroundColor: "#111",
            color: "white",
            minHeight: "32px",
            cursor: isDragging ? "grabbing" : "grab",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              flex: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{ opacity: 0.8, fontSize: "11px" }}
            >
              {isConnecting
                ? "Connecting..."
                : isConnected
                  ? "Live"
                  : "Disconnected"}
            </Typography>
            {connectionTime && isConnected && (
              <>
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.6, fontSize: "11px" }}
                >
                  •
                </Typography>
                <Duration startTime={connectionTime} />
              </>
            )}
          </Box>
        </Box>

        <Box
          id="video-pip-container"
          sx={{
            height: "calc(100% - 32px)",
            width: "100%",
            position: "relative",
          }}
        ></Box>
      </Box>
    </div>
  );
};

export default ActiveVideoCallModule;
